import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconExternalLink, IconHistory } from "@tabler/icons-react";

import { api } from "../api/client";
import { EmptyState, ErrorState, LoadingState } from "../components/ui";
import { useMarket } from "../hooks/useMarket";
import { monoStyle } from "../theme";

export function PreviousConfigsPage() {
  const qc = useQueryClient();
  // V3 multi-market: scope the list + activation to the currently
  // focused market so operators only see snapshots relevant to it
  // (currently active for that market OR ever bound to one of its
  // windows) and clicking "Set active" updates the per-market pointer.
  const marketCtx = useMarket();
  const marketId = marketCtx.marketId;
  const marketLabel = marketCtx.market?.display_name ?? marketCtx.market?.short_name ?? marketId;
  const [selected, setSelected] = useState<{
    config_version_id: string;
    config: Record<string, unknown>;
    created_at: string;
    created_by: string | null;
    notes: string | null;
    is_active: boolean;
  } | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["config", "versions", marketId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.config.versions({ cursor: pageParam, limit: 25, market_id: marketId }),
    getNextPageParam: (last) => (last.page.has_more ? last.page.next_cursor ?? undefined : undefined),
    staleTime: 30_000,
    enabled: Boolean(marketId),
  });

  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  // Per-config trading stats (wins/losses/total resolved orders placed
  // while each config version was active). Keyed by config_version_id.
  const statsQ = useQuery({
    queryKey: ["config", "version-stats", marketId],
    queryFn: () => api.config.versionStats({ market_id: marketId }),
    staleTime: 30_000,
    enabled: Boolean(marketId),
  });
  const statsById = new Map(
    (statsQ.data?.items ?? []).map((s) => [s.config_version_id, s]),
  );

  const activateConfig = useMutation({
    mutationFn: (configVersionId: string) =>
      api.markets.activateConfig(marketId, configVersionId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["config", "versions"] });
      qc.invalidateQueries({ queryKey: ["markets", marketId, "config-active"] });
      qc.invalidateQueries({ queryKey: ["config", "active"] });
      notifications.show({
        color: "green",
        title: `Config activated for ${marketLabel}`,
        message: `Active: ${res.config_version_id.slice(0, 18)}... (applies ${new Date(res.applies_from_window_start).toLocaleString()})`,
      });
      setSelected((prev) => (prev ? { ...prev, is_active: prev.config_version_id === res.config_version_id } : prev));
    },
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error as Error} onRetry={() => q.refetch()} />;

  const openDetails = (cfg: {
    config_version_id: string;
    config: Record<string, unknown>;
    created_at: string;
    created_by: string | null;
    notes: string | null;
    is_active: boolean;
  }) => {
    setSelected({
      config_version_id: cfg.config_version_id,
      config: cfg.config,
      created_at: cfg.created_at,
      created_by: cfg.created_by,
      notes: cfg.notes,
      is_active: cfg.is_active,
    });
  };

  return (
    <Stack gap="md">
      <Modal
        opened={selected !== null}
        onClose={() => setSelected(null)}
        title="Snapshot Settings"
        size="xl"
      >
        {selected ? (
          <Stack gap="sm">
            <Group justify="space-between" gap="md">
              <Text fw={600}>
                {typeof selected.config?.config_name === "string" ? String(selected.config.config_name) : "—"}
              </Text>
              {selected.is_active ? (
                <Badge color="cyan">active</Badge>
              ) : (
                <Badge color="gray" variant="light">snapshot</Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              Version: <Code style={monoStyle}>{selected.config_version_id}</Code>
            </Text>
            <Text size="sm" c="dimmed">
              Created: <Text span style={monoStyle}>{new Date(selected.created_at).toLocaleString()}</Text>
            </Text>
            <Text size="sm" c="dimmed">By: {selected.created_by ?? "—"}</Text>
            <Text size="sm" c="dimmed">Notes: {selected.notes ?? "—"}</Text>
            <Group justify="flex-end">
              <Button
                size="xs"
                disabled={selected.is_active}
                loading={activateConfig.isPending}
                onClick={() => activateConfig.mutate(selected.config_version_id)}
              >
                {selected.is_active ? "Currently active" : "Set as active"}
              </Button>
            </Group>
            <ScrollArea maw="100%" h={420} type="auto">
              <Code block style={{ ...monoStyle, fontSize: 12, whiteSpace: "pre" }}>
                {JSON.stringify(selected.config, null, 2)}
              </Code>
            </ScrollArea>
          </Stack>
        ) : null}
      </Modal>

      <Group justify="space-between" align="flex-end">
        <Group gap="xs">
          <IconHistory size={18} />
          <Title order={3}>Previous Configs</Title>
          <Badge variant="light" color="blue">{marketLabel}</Badge>
        </Group>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            disabled={!q.hasNextPage || q.isFetchingNextPage}
            onClick={() => q.fetchNextPage()}
          >
            {q.isFetchingNextPage ? "loading..." : q.hasNextPage ? "load more" : "all loaded"}
          </Button>
        </Group>
      </Group>

      <Card withBorder padding="md">
        {rows.length === 0 ? (
          <EmptyState label={`No config snapshots for ${marketLabel} yet.`} />
        ) : (
          <ScrollArea h={560}>
            <Table highlightOnHover horizontalSpacing="md" verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Stats</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>By</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Notes</Table.Th>
                  <Table.Th>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((cfg) => {
                  const cfgName = typeof (cfg.config as Record<string, unknown>)?.config_name === "string"
                    ? String((cfg.config as Record<string, unknown>).config_name)
                    : "—";
                  const NAME_MAX = 34;
                  const cfgNameShort =
                    cfgName.length > NAME_MAX ? `${cfgName.slice(0, NAME_MAX - 1)}…` : cfgName;
                  const stats = statsById.get(cfg.config_version_id);
                  const rowConfig = {
                    config_version_id: cfg.config_version_id,
                    config: cfg.config as Record<string, unknown>,
                    created_at: cfg.created_at,
                    created_by: cfg.created_by ?? null,
                    notes: cfg.notes ?? null,
                    is_active: cfg.is_active,
                  };
                  return (
                    <Table.Tr key={cfg.config_version_id}>
                      <Table.Td maw={280}>
                        <Tooltip
                          label={cfgName}
                          withArrow
                          multiline
                          maw={360}
                          disabled={cfgName === cfgNameShort}
                        >
                          <Button
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            px={0}
                            onClick={() => openDetails(rowConfig)}
                            styles={{ label: { whiteSpace: "nowrap" } }}
                          >
                            {cfgNameShort}
                          </Button>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="Open in Manual Simulator (new tab)" withArrow>
                          <Button
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            px={0}
                            component="a"
                            href={`#/analytics/manual-simulator?config_version_id=${encodeURIComponent(cfg.config_version_id)}`}
                            target="_blank"
                            rel="noreferrer"
                            rightSection={<IconExternalLink size={12} />}
                          >
                            <Code style={monoStyle}>{cfg.config_version_id}</Code>
                          </Button>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        {statsQ.isLoading ? (
                          <Text size="sm" c="dimmed">…</Text>
                        ) : stats && stats.total_orders > 0 ? (
                          <Tooltip
                            label={`${stats.wins} wins / ${stats.losses} losses / ${stats.total_orders} total resolved orders while active`}
                            withArrow
                          >
                            <Group gap={4} wrap="nowrap">
                              <Text size="sm" c="teal.4" fw={600}>{stats.wins}W</Text>
                              <Text size="sm" c="dimmed">/</Text>
                              <Text size="sm" c="red.4" fw={600}>{stats.losses}L</Text>
                              <Text size="sm" c="dimmed">/</Text>
                              <Text size="sm" c="dimmed">{stats.total_orders}</Text>
                            </Group>
                          </Tooltip>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td style={monoStyle}>{new Date(cfg.created_at).toLocaleString()}</Table.Td>
                      <Table.Td>{cfg.created_by ?? "—"}</Table.Td>
                      <Table.Td>
                        {cfg.is_active ? (
                          <Badge color="cyan">active</Badge>
                        ) : (
                          <Badge color="gray" variant="light">snapshot</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={cfg.notes ? undefined : "dimmed"}>
                          {cfg.notes ?? "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="compact-xs"
                          variant={cfg.is_active ? "light" : "filled"}
                          disabled={cfg.is_active}
                          loading={activateConfig.isPending}
                          onClick={() => activateConfig.mutate(cfg.config_version_id)}
                        >
                          {cfg.is_active ? "Active" : "Set active"}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}
